/**
 * Shared catalog for Getting around how-to GIFs (SEO pages + in-app guide).
 * Assets live at /marketing/gifs/{desktop|mobile}/{light|dark|sepia}/{id}.gif
 */

export type GettingAroundId =
  | "start-from-scratch"
  | "navigate-workspaces"
  | "add-screen-to-row"
  | "open-analysis-tools"
  | "pin-a-tool"
  | "search-manuscript"
  | "create-a-tool";

export type HowtoGifLayout = "desktop" | "mobile";
export type HowtoGifTheme = "light" | "dark" | "sepia";

export type GettingAroundEntry = {
  id: GettingAroundId;
  title: string;
  body: string;
  alt: string;
  howToHref: string;
};

export const GETTING_AROUND_ENTRIES: GettingAroundEntry[] = [
  {
    id: "start-from-scratch",
    title: "Start a blank novel",
    body: "From the dashboard, create a new project, open it, add your first chapter, and begin typing. The book saves as you write.",
    alt: "Creating a new Novilot project, adding the first chapter, and typing the opening line.",
    howToHref: "/how-to/start-writing-a-novel-in-novilot",
  },
  {
    id: "navigate-workspaces",
    title: "Move between Plan, Draft, Revise, and Prepare",
    body: "The four stages at the top are the main navigation. Each one swaps the screen row below for the screens that suit that kind of work.",
    alt: "Switching Novilot workspaces from Plan to Draft to Revise to Prepare while the screen row changes.",
    howToHref: "/how-to/navigate-novilot",
  },
  {
    id: "add-screen-to-row",
    title: "Add a screen to the row",
    body: "The plus opens every screen, searchable and grouped. Add Continuity, Analysis, or any other screen to this stage's row so it stays one click away.",
    alt: "Opening the plus picker, searching screens, and adding one to the Novilot screen row.",
    howToHref: "/how-to/add-screens-and-tools",
  },
  {
    id: "open-analysis-tools",
    title: "Open analysis tools",
    body: "Open the Analysis screen, search the tool grid, and open a card. Featured deep reads sit at the top; your custom tools appear under Your tools.",
    alt: "Opening the Analysis screen in Novilot, searching tools, and opening a tool card.",
    howToHref: "/how-to/add-screens-and-tools",
  },
  {
    id: "pin-a-tool",
    title: "Pin a tool you reuse",
    body: "On a tool you open often, choose Pin. It appears under Pinned on that grid so you do not hunt for it next time. This is separate from adding a screen to the row.",
    alt: "Pinning an analysis tool in Novilot so it appears in the Pinned section of the tool grid.",
    howToHref: "/how-to/pin-tools-in-novilot",
  },
  {
    id: "search-manuscript",
    title: "Search the whole book",
    body: "The magnifier searches your prose across chapters, not just titles. Use Exact words for a phrase, or By meaning when you remember the idea but not the wording.",
    alt: "Opening manuscript search in Novilot, running an exact-words query, and viewing matching results.",
    howToHref: "/how-to/search-your-manuscript",
  },
  {
    id: "create-a-tool",
    title: "Build a tool of your own",
    body: "Open Build a tool, describe what you want to know about your book, review the draft Novilot prepares, and keep it. It then lives in your tool grid like any other check.",
    alt: "Opening Build a tool in Novilot, choosing an example prompt, reviewing the drafted tool, and keeping it.",
    howToHref: "/how-to/create-a-custom-tool",
  },
];

export function gettingAroundById(id: GettingAroundId): GettingAroundEntry {
  const entry = GETTING_AROUND_ENTRIES.find((e) => e.id === id);
  if (!entry) {
    throw new Error(`Unknown getting-around id: ${id}`);
  }
  return entry;
}

export function howtoGifSrc(
  layout: HowtoGifLayout,
  theme: HowtoGifTheme,
  id: GettingAroundId
): string {
  return `/marketing/gifs/${layout}/${theme}/${id}.gif`;
}

export function howtoGifPosterSrc(
  layout: HowtoGifLayout,
  theme: HowtoGifTheme,
  id: GettingAroundId
): string {
  return `/marketing/gifs/${layout}/${theme}/${id}-poster.png`;
}

export function howtoWebmSrc(
  layout: HowtoGifLayout,
  theme: HowtoGifTheme,
  id: GettingAroundId
): string {
  return `/marketing/webm/${layout}/${theme}/${id}.webm`;
}

/**
 * Fallback order matches marketing tour PNGs: prefer current layout+theme,
 * then light on that layout, then the other layout.
 */
export function buildHowtoGifCandidates(
  isMobile: boolean,
  theme: HowtoGifTheme,
  id: GettingAroundId
): string[] {
  const p = (layout: HowtoGifLayout, t: HowtoGifTheme) => howtoGifSrc(layout, t, id);
  if (isMobile) {
    return [
      p("mobile", theme),
      p("mobile", "light"),
      p("desktop", theme),
      p("desktop", "light"),
    ];
  }
  return [
    p("desktop", theme),
    p("desktop", "light"),
    p("mobile", theme),
    p("mobile", "light"),
  ];
}

export function buildHowtoWebmCandidates(
  isMobile: boolean,
  theme: HowtoGifTheme,
  id: GettingAroundId
): string[] {
  const p = (layout: HowtoGifLayout, t: HowtoGifTheme) => howtoWebmSrc(layout, t, id);
  if (isMobile) {
    return [
      p("mobile", theme),
      p("mobile", "light"),
      p("desktop", theme),
      p("desktop", "light"),
    ];
  }
  return [
    p("desktop", theme),
    p("desktop", "light"),
    p("mobile", theme),
    p("mobile", "light"),
  ];
}

export function buildHowtoPosterCandidates(
  isMobile: boolean,
  theme: HowtoGifTheme,
  id: GettingAroundId
): string[] {
  const p = (layout: HowtoGifLayout, t: HowtoGifTheme) =>
    howtoGifPosterSrc(layout, t, id);
  if (isMobile) {
    return [
      p("mobile", theme),
      p("mobile", "light"),
      p("desktop", theme),
      p("desktop", "light"),
    ];
  }
  return [
    p("desktop", theme),
    p("desktop", "light"),
    p("mobile", theme),
    p("mobile", "light"),
  ];
}
